import React, { useMemo, useState } from 'react'

import { H3, Text } from '@mxenabled/mxui'

import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'

import { TipSection } from './TipSection'
import type { TipSectionProps } from './TipSection'

interface SubHeading {
  category_guid: string
  description: string
  merchant_guid: string
}

export interface DrawerBeat {
  heading?: string
  subHeading?: SubHeading
}

interface TabData {
  ariaLabel?: string
  formattedAmount: string
  label: string
  value: number
}

export interface ChartDrawerTemplateProps {
  ariaLabel: string
  beat: DrawerBeat
  chart: (selectedIndex: number, onBarClick: (index: number) => void) => React.ReactNode
  children?: (selectedIndex: number) => React.ReactNode
  data: TabData[]
  defaultSelectedIndex?: number
  icon?: React.ReactNode
  onTabChange?: (index: number) => void
  tipSection?: TipSectionProps
}

export const ChartDrawerTemplate = ({
  ariaLabel,
  beat,
  chart,
  children,
  data,
  defaultSelectedIndex,
  icon,
  onTabChange,
  tipSection,
}: ChartDrawerTemplateProps) => {
  const { heading, subHeading } = beat

  const initialIndex = useMemo(
    () => defaultSelectedIndex ?? (data.length ? data.length - 1 : 0),
    [defaultSelectedIndex, data.length],
  )

  const [tabValue, setTabValue] = useState(initialIndex)

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    onTabChange?.(newValue)
    setTabValue(newValue)
  }

  const handleBarClick = (dataIndex: number) => {
    setTabValue(dataIndex)
  }

  return (
    <Box sx={{ bgcolor: 'background.paper', width: '100%' }}>
      <Tabs aria-label={ariaLabel} onChange={handleTabChange} value={tabValue} variant="fullWidth">
        {data.map((item, index) => (
          <Tab
            id={`transaction-details-tab-${index}`}
            key={`${item.label}-${index}`}
            label={item.label}
            sx={{ minWidth: 'auto', px: 0 }}
          />
        ))}
      </Tabs>

      <Stack sx={{ pt: 16, px: 24 }}>
        {(heading || subHeading) && (
          <Stack sx={{ gap: 4 }}>
            {heading && <H3>{heading}</H3>}

            {subHeading && (
              <Stack sx={{ alignItems: 'center', flexDirection: 'row', gap: 8 }}>
                {icon}
                <Text textTransform="uppercase" variant="body2">
                  {subHeading.description}
                </Text>
              </Stack>
            )}
          </Stack>
        )}
      </Stack>

      {chart?.(tabValue, handleBarClick)}

      {tipSection && <TipSection {...tipSection} />}

      {children?.(tabValue)}
    </Box>
  )
}
